import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { trackedSet } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismTable, styleEntry } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import type { Hotelinfo } from 'frontend-utils/models';
import OverrideIndicator from 'lumen/components/hotel/pricing-assistant/override-indicator';
import HotelPricingAssistantTableCellsDayTableThDayTableValueCell, {
  type CustomColumn,
} from 'lumen/components/hotel/pricing-assistant/table/cells/day-table/th-day-table-value-cell';
import HotelPricingAssistantTableCellsTdRateCellDispatcher from 'lumen/components/hotel/pricing-assistant/table/cells/td-rate-cell-dispatcher';
import HotelPricingAssistantTableCellsTdRoom from 'lumen/components/hotel/pricing-assistant/table/cells/td-room';
import HotelPricingAssistantTableModalsEditOverrideModalEditOverrideModal from 'lumen/components/hotel/pricing-assistant/table/modals/edit-override-modal/edit-override-modal';
import type { Carate, Period, Rate } from 'lumen/models';
import type AccessService from 'lumen/services/access';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { LOCAL_STORAGE_KEYS } from 'lumen/services/local-storage';
import {
  DAY_TABLE_SIZES,
  HEADER_HEIGHT,
  SUB_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import {
  OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER,
  OVERRIDE_TYPES_USAGE_TRACKING,
  PA_REF_DAYS_AGO,
  type PricePreviewRooms,
  RECOMMENDATION_ACTION_STATES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { getPeriodsInRange } from 'lumen/utils/dates/moment-helpers';

import styles from './table-pricing-manager.module.css';

export interface TablePricingManagerCustomColumn extends CustomColumn {
  isColumnUpdating: boolean;
  refDaysAgo: number;
}

type DateMap<T> = Record<string, T | undefined>;

interface CompetitorRateRow extends CustomEmberTableRow {
  dates: DateMap<Rate>;
  id: string;
  room: { roomName: string };
}

export interface TablePricingManagerCustomRow extends CustomEmberTableRow {
  deleteOverride: (carate: Carate) => Promise<void>;
  isOwnRateRow?: boolean;
  openEditOverrideModal: (carate: Carate) => void;
  room: {
    roomName: string;
  };
}

interface OwnRateRow extends TablePricingManagerCustomRow {
  children: CompetitorRateRow[];
  dates: Record<string, Carate | undefined>;
  isCollapsed: boolean;
  pmsRoomtype: string;
  roomCategory: string;
}

interface HotelPricingAssistantTableTablePricingManagerSignature {
  Args: {
    competitorsForCurrentHotelAndOTA: Hotelinfo[] | null;
    flattenedRoomOffsetHierarchy: string[];
    isHeaderLoading: boolean;
    isLoading: boolean;
    model: { competitorsPeriods: Period[]; periods: Period[] };
    onDayHeaderClick?: (period: Period) => void;
    range: {
      end: Dayjs;
      start: Dayjs;
    };
    rateCode: string | null;
    refDaysAgo: number;
  };
}

export default class HotelPricingAssistantTableTablePricingManager extends Component<HotelPricingAssistantTableTablePricingManagerSignature> {
  @service declare access: AccessService;
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service
  declare pricingAssistantHandleRecommendationActionsService: Services['pricing-assistant-handle-recommendation-actions-service'];
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];

  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked carateToEditOverride: Carate | null = null;
  @tracked isBaseRoomRowCollapsed = false;

  columnIdsInLoading = trackedSet<string>();

  COLUMNS = {
    ROOM: 'room',
  } as const;

  @cached
  get baseRoomCompetitorRateRows(): CompetitorRateRow[] {
    const { competitorsForCurrentHotelAndOTA, model } = this.args;

    if (!competitorsForCurrentHotelAndOTA) {
      return [];
    }

    // Create a row for each competitor
    const competitorRateRows = competitorsForCurrentHotelAndOTA.map(
      (hotelInfo): CompetitorRateRow => ({
        id: hotelInfo.id!,
        [this.COLUMNS.ROOM]: { roomName: hotelInfo.name },
        dates: {},
      }),
    );

    model.competitorsPeriods.forEach((period) => {
      const dateString = period.day.date.format('YYYY-MM-DD');

      // Create a map where each key is a [hotel.id] and the value is a Rate
      const ratesByHotel = Object.fromEntries(
        period.competitorRates.map((rate) => [rate.hotelinfo.id, rate]),
      ) as Record<string, Rate>;

      // For each competitor row, fill in the dates with the corresponding rates
      competitorRateRows.forEach((row) => {
        row.dates[dateString] = ratesByHotel[row.id] ?? undefined;
      });
    });

    return competitorRateRows;
  }

  get columns(): CustomEmberTableColumn[] {
    const columns: CustomEmberTableColumn[] = [
      {
        name: '',
        valuePath: this.COLUMNS.ROOM,
        textAlign: 'left' as const,
        maxWidth: DAY_TABLE_SIZES.NAME_MAX_WIDTH,
        tdComponent: HotelPricingAssistantTableCellsTdRoom,
      },
    ];

    const rangeDays = this.rangeDays;
    rangeDays.forEach((date, index) => {
      const columnId = date.format('YYYY-MM-DD');
      columns.push({
        name: '',
        valuePath: `dates.${columnId}`,
        textAlign: 'right' as const,
        width: DAY_TABLE_SIZES.VALUE_WIDTH,
        tdComponent: HotelPricingAssistantTableCellsTdRateCellDispatcher,
        date,
        id: columnId,
        isColumnUpdating: this.columnIdsInLoading.has(columnId),
        isHeaderLoading: this.args.isHeaderLoading,
        isLastColumn: index === rangeDays.length - 1,
        onDayHeaderClick: this.args.onDayHeaderClick,
        period: this.args.model.periods.find((period) =>
          period.day.date.isSame(date, 'day'),
        ),
        refDaysAgo: this.args.refDaysAgo,
        thComponent: HotelPricingAssistantTableCellsDayTableThDayTableValueCell,
      });
    });

    return columns;
  }

  get pricePreviewRooms(): PricePreviewRooms {
    const { periods = [] } = this.args.model;
    const foundPeriod = periods.find(
      (period) => period.day === this.carateToEditOverride?.period.day,
    );
    const ownHotelCarates = foundPeriod?.ownHotelCarates;

    return this.rows.map((row) => {
      const matchingCarate = ownHotelCarates?.find(
        (carate) =>
          carate.roomName === row.room.roomName &&
          carate.rateCode === this.args.rateCode,
      );

      return {
        roomName: row.room.roomName,
        pmsRoomType: row.pmsRoomtype,
        overrideType: matchingCarate?.override?.type ?? null,
        showOverride: Boolean(matchingCarate?.showOverride),
        currentPrice: matchingCarate?.dynamicRatePricingPriceToShow,
        formattedCurrentPrice:
          matchingCarate?.formattedDynamicRatePricingPriceToShow,
      };
    });
  }

  get rangeDays(): Dayjs[] {
    return getPeriodsInRange(this.args.range.start, this.args.range.end, 'day');
  }

  @cached
  get rows(): OwnRateRow[] {
    if (this.args.isLoading || !this.args.model.periods[0]) {
      return [];
    }

    const period = this.args.model.periods[0];
    const sortedRoomCarates = this.getSortedRoomCarates(period);

    let filteredAndSortedRoomCarates = sortedRoomCarates;

    // Filter by rate code
    if (this.access.hasPricingAssistantDynamicRatePricingFeature) {
      filteredAndSortedRoomCarates = sortedRoomCarates.filter(
        (carate: Carate) => carate.rateCode === this.args.rateCode,
      );
    }

    return filteredAndSortedRoomCarates.map((carate: Carate) => {
      const { roomName, pmsRoomtype } = carate;
      const isBaseRoom =
        this.hotelAndMarketService.hotel.caxxxPmsSettings
          .pms_leading_room_id === roomName;
      const roomCategory = carate.cemaCategory;
      const dates: Record<string, Carate | undefined> = {};
      const children = isBaseRoom ? this.baseRoomCompetitorRateRows : [];

      this.args.model.periods.forEach((periodItem) => {
        const dateString = periodItem.day.date.format('YYYY-MM-DD');
        const ownCaratesForRoomCategory =
          periodItem.ownCaratesGroupedByCemaCategory[roomCategory];

        let foundCarate;

        if (this.access.hasPricingAssistantDynamicRatePricingFeature) {
          foundCarate = ownCaratesForRoomCategory?.find(
            (c: Carate) =>
              c.roomName === roomName && c.rateCode === this.args.rateCode,
          );
        } else {
          foundCarate = ownCaratesForRoomCategory?.find(
            (c: Carate) => c.roomName === roomName,
          );
        }

        dates[dateString] = foundCarate;
      });

      return {
        id: roomName,
        pmsRoomtype,
        [this.COLUMNS.ROOM]: { roomName, isBaseRoom },
        isCollapsed: isBaseRoom && this.isBaseRoomRowCollapsed,
        dates,
        children,
        roomCategory,
        isOwnRateRow: true,
        openEditOverrideModal: this.openEditOverrideModal.bind(this),
        deleteOverride: this.deleteOverride.bind(this),
      };
    });
  }

  /* Reserve space for: 2 nav headers + sub-header + main content padding bottom + main content padding top + override indicator row */
  get tableReservedHeight(): string {
    const overrideIndicatorRowHeight = 28;
    return `${HEADER_HEIGHT + SUB_HEADER_HEIGHT + 16 + 8 + overrideIndicatorRowHeight}px`;
  }

  constructor(
    owner: Owner,
    args: HotelPricingAssistantTableTablePricingManagerSignature['Args'],
  ) {
    super(owner, args);

    // Initialize the collapse state from local storage
    const storedValue = this.localStorage.getItem(
      LOCAL_STORAGE_KEYS.IS_PRIMA_TABLE_BASE_ROOM_ROW_COLLAPSED,
    );

    this.isBaseRoomRowCollapsed = storedValue
      ? (JSON.parse(storedValue) as boolean)
      : false;
  }

  getSortedRoomCarates(period: Period): Carate[] {
    const { flattenedRoomOffsetHierarchy } = this.args;

    // Rooms are sorted based on flattened room offset hierarchy
    if (flattenedRoomOffsetHierarchy.length) {
      return Object.values(period.ownCaratesGroupedByCemaCategory)
        .flat()
        .sort((a, b) => {
          const indexA = flattenedRoomOffsetHierarchy.indexOf(a.pmsRoomtype);
          const indexB = flattenedRoomOffsetHierarchy.indexOf(b.pmsRoomtype);
          return indexA - indexB;
        });
    }

    // If no room offset hierarchy is configured, rooms are sorted based on their average price priority within their sorted category (same as calendar day view)
    return Object.values(period.ownCaratesGroupedByCemaCategory)
      .flat()
      .sort((a, b) => {
        const categoryAIndex = OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER.indexOf(
          a.cemaCategory,
        );
        const categoryBIndex = OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER.indexOf(
          b.cemaCategory,
        );

        if (categoryAIndex !== categoryBIndex) {
          return categoryAIndex - categoryBIndex;
        }

        return a.averagePricePriority - b.averagePricePriority;
      });
  }

  @action
  closeEditOverrideModal(): void {
    this.carateToEditOverride = null;
  }

  @action
  async deleteOverride(carate: Carate): Promise<void> {
    if (!carate.override || typeof carate.override.id !== 'string') {
      return;
    }

    this.usageAnalytics.track('Autopilot Override Disabled', {
      eventTrigger: 'table-tile',
      overrideType: OVERRIDE_TYPES_USAGE_TRACKING[carate.override.type],
    });

    const columnId = carate.period.fromDate.format('YYYY-MM-DD');

    this.columnIdsInLoading.add(columnId);

    const removalResult =
      await this.pricingAssistantDataRequestService.deleteOverridesTask.perform(
        [carate.override.id],
      );

    if (!removalResult || !removalResult.isSuccess) {
      this.toast.error(this.intl.t('general.error_short'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
      return;
    }

    this.toast.success(this.intl.t('general.changes_saved'), {
      timeout: TOAST_TIMEOUT_LONG,
      showIcon: true,
    });

    const [carecommendations] =
      (await this.pricingAssistantDataRequestService.pricingAssistantRatesAndRecommendationsSingleDayRequestTask.perform(
        this.hotelAndMarketService.hotel.id as string,
        carate.period.fromDate.format('YYYY-MM-DD'),
        this.hotelAndMarketService
          .getLocalTime()
          .subtract(PA_REF_DAYS_AGO, 'day')
          .format('YYYY-MM-DD'),
      )) ?? [];

    this.columnIdsInLoading.delete(columnId);

    if (!carecommendations?.length) {
      return;
    }

    this.pricingAssistantRecommendationActionPoller.addRecommendationsAffectedByOverridePush(
      carecommendations,
    );

    const carecommendationAndCaratePairs = carecommendations.map(
      (carecommendation) => ({
        carecommendation,
        carate: carecommendation.carate,
      }),
    );

    void this.pricingAssistantHandleRecommendationActionsService.handleCreateRecommendationActionBulkPush(
      carecommendationAndCaratePairs,
      RECOMMENDATION_ACTION_STATES.ACCEPTED,
      {},
    );
  }

  @action
  openEditOverrideModal(carate: Carate): void {
    this.carateToEditOverride = carate;
  }

  @action
  saveBaseRoomRowCollapseStateInLocalStorage(): void {
    this.localStorage.setItem(
      LOCAL_STORAGE_KEYS.IS_PRIMA_TABLE_BASE_ROOM_ROW_COLLAPSED,
      JSON.stringify(!this.isBaseRoomRowCollapsed),
    );

    this.isBaseRoomRowCollapsed = !this.isBaseRoomRowCollapsed;
  }

  <template>
    <div class="mb-200 flex items-center justify-end gap-200">
      <div class="flex items-center gap-50">
        <OverrideIndicator />
        <p class="text-200--regular">
          {{t "pricing_assistant.manual_overrides"}}
        </p>
      </div>
      <div class="flex items-center gap-50">
        <OverrideIndicator @variant="automation-off" />
        <p class="text-200--regular">
          {{t "pricing_manager.automation_off"}}
        </p>
      </div>
    </div>

    <div
      class={{styles.table}}
      style={{styleEntry "--table-reserved-height" this.tableReservedHeight}}
    >
      <PrismTable
        @columns={{this.columns}}
        @fillMode="first-column"
        @isLoading={{@isLoading}}
        @nrOfEmptyRows={{25}}
        @nrOfLoadingRows={{2}}
        @onRowCollapseToggle={{this.saveBaseRoomRowCollapseStateInLocalStorage}}
        @rows={{this.rows}}
        @widthConstraint="eq-container"
      />
    </div>

    {{#if this.carateToEditOverride}}
      <HotelPricingAssistantTableModalsEditOverrideModalEditOverrideModal
        @carate={{this.carateToEditOverride}}
        @onCloseModal={{this.closeEditOverrideModal}}
        @pricePreviewRooms={{this.pricePreviewRooms}}
        @rateCode={{@rateCode}}
      />
    {{/if}}
  </template>
}
